import {
  IPuzzle,
  ICell,
  IClue,
  CellType,
  CellState,
  IPuzzleMetaData,
  SpacebarKeySettings,
} from 'js/crossword/types';
import { wrapIndex } from 'framework/utils/array-utils';
import { getOther } from 'framework/utils/misc-utils';
import { handleArrowNavigation } from 'js/crossword/puzzleDataHandlers/handleArrowNavigation';
import { KeyCode } from 'framework/Keyboard/KeyCodes';
import { saveData, loadData } from 'js/crossword/saveRestorePuzzle';
import {
  getCurrentCell,
  getCluesWithCellIds,
  getClue,
  getCurrentClue,
  getOtherDirectionClue,
  getNextCellInClue,
} from 'js/crossword/puzzleDataHandlers/helpers';
import { getSettingsData } from 'js/crossword/settingsData';

export class Puzzle {
  public readonly data: IPuzzle;
  public revealedPuzzle = false;

  constructor(data: IPuzzle) {
    this.data = data;

    setInterval(() => {
      if (!this.data.paused) {
        this.data.time += 1000;
      }
      this.saveDataHandler();
    }, 1000);

    this.start();
  }

  // REFACTOR
  public start(): void {
    loadData(this.data);
    this.setSelection(this.data.currentClueId, this.data.currentCellId);

    this.updateFillPercantage();
  }

  private saveDataHandler(): void {
    saveData(this.data);
  };

  public getData(): IPuzzle {
    return this.data;
  }

  public getMetaData(): IPuzzleMetaData {
    return this.data.metaData;
  }

  public clone(): Puzzle {
    return new Puzzle(JSON.parse(JSON.stringify(this.data)));
  }

  public getTime(): number {
    return this.data.time;
  }

  public getID(): string {
    return this.data.id;
  }

  public setPause(value: boolean): void {
    if (this.data.paused === value) return;
    this.data.paused = value;
  }

  public getPause(): boolean {
    return this.data.paused;
  }

  public getBoardWidth(): number {
    return this.data.columns;
  }

  public getBoardHeight(): number {
    return this.data.rows;
  }

  public getAllCells(): ICell[] {
    return this.data.cells;
  }

  public getAllClues(): IClue[] {
    return this.data.clues;
  }

  public fillCell(letter: string): void {
    if (!this.isInputEnabled()) return;

    const cell = this.getCell(this.data.currentCellId);
    if (cell.state !== CellState.Revealed) {
      if (this.data.rebus) {
        cell.letter += letter;
      } else {
        cell.letter = letter;
        this.updateFillPercantage();
      }
      cell.state = CellState.Normal;
      if (this.data.autocheck) this.checkLetter();
    }

    if (!this.data.rebus) this.moveCurrentCellInClueAfterInput(1);

    this.saveDataHandler();
  }

  public eraseCell(): void {
    if (!this.isInputEnabled()) return;

    const cell = this.getCell(this.data.currentCellId);
    if (!cell) return;

    if (cell.letter === '' || cell.state === CellState.Revealed) {
      this.moveCurrentCellInClueAfterInput(-1);
    } else {
      if (this.data.rebus && cell.letter.length > 1) {
        cell.letter = cell.letter.slice(0, -1);
      } else {
        cell.letter = '';
        this.updateFillPercantage();
      }
      cell.state = CellState.Normal;
    }

    this.saveDataHandler();
  }

  public toggleDirection(): void {
    const otherDirectionClue = getOtherDirectionClue(this.data);
    if (otherDirectionClue) {
      this.setSelection(otherDirectionClue.id, this.data.currentCellId);
    }
  }

  public handleSpacebar(): void {
    if (!this.isInputEnabled()) return;

    switch (getSettingsData().spaceBar) {
      case SpacebarKeySettings.ToggleDirection:
        this.toggleDirection();
        break;
      case SpacebarKeySettings.ClearAndMoveAhead:
        this.clearLetter(this.data.currentCellId);
        const nextCellId = getNextCellInClue(getCurrentClue(this.data), this.data.currentCellId)
        this.setSelection(this.data.currentClueId, nextCellId);
    }
  }

  public handleArrowNavigation(keycode: KeyCode): void {
    if (!this.isInputEnabled()) return;

    const direction = {
      [KeyCode.UP]: [0, -1],
      [KeyCode.DOWN]: [0, 1],
      [KeyCode.LEFT]: [-1, 0],
      [KeyCode.RIGHT]: [1, 0],
    }[keycode];

    if (!direction) return;

    const [clue, cell] = handleArrowNavigation(this.data, direction as [number, number]);
    this.setSelection(clue.id, cell.id);
  }

  public changeClue(indexChange: number): void {
    const clues = this.getAllClues();
    const clueIndex = this.data.clues.indexOf(getCurrentClue(this.data));
    const nextClue = clues[wrapIndex(clues, clueIndex + indexChange)];
    this.setSelection(nextClue.id, nextClue.cells[0]);
  }

  public setCurrentClue(clueId: string): void {
    this.setSelection(clueId, getClue(this.data, clueId).cells[0]);
  }

  public handleCellClick(id: string): void {
    const currentClue = getCurrentClue(this.data);

    if (currentClue && currentClue.cells.includes(id) && this.data.currentCellId !== id) {
      this.setSelection(getCurrentClue(this.data).id, id);
      return;
    }

    this.setClueFromCell(id);
  }

  public toggleAutocheck(): void {
    this.data.autocheck = !this.data.autocheck;
  }

  public toggleRebus(): void {
    this.data.rebus = !this.data.rebus;
  }

  public clearIncomplete(): void {
    // TODO
  }

  public clearLetter(cellId: string, alsoRevealed: boolean = false, skipFillUpdate: boolean = false): void {
    const cell = this.getCell(cellId);
    if (!cell) return;
    if (cell.letter === '') return;
    if (cell.state === CellState.Revealed && !alsoRevealed) return;

    cell.letter = '';
    cell.state = CellState.Normal;
    if (!skipFillUpdate) this.updateFillPercantage();
  }

  public clearWord(): void {
    getCurrentClue(this.data).cells.forEach((cellId) => this.clearLetter(cellId, false, true));
    this.updateFillPercantage();
  }

  public clearPuzzle(clearTimer: boolean = false): void {
    this.revealedPuzzle = false;
    this.getAllCells().forEach((cell) => this.clearLetter(cell.id, true, true));
    this.updateFillPercantage();
    if (clearTimer) this.clearTimer();
  }

  public clearTimer(): void {
    this.data.time = 0;
  }

  public checkLetter(clueIndex?: number, cellId?: string): void {
    const cell = cellId ? this.getCell(cellId) : getCurrentCell(this.data);
    if (cell.state === CellState.Revealed) return;

    const clue = clueIndex !== undefined ? this.getAllClues()[clueIndex] : getCurrentClue(this.data);
    const correctLetter = cell.solution;

    if (cell.letter === correctLetter) {
      cell.state = CellState.Correct;
    } else if (cell.letter !== '' && cell.letter !== correctLetter) {
      cell.state = CellState.Error;
    }
  }

  public checkWord(clueIndex?: number): void {
    const checkedClueIndex = clueIndex ?? this.getAllClues().indexOf(getCurrentClue(this.data));
    const clue = this.getAllClues()[checkedClueIndex];
    clue.cells.forEach((cellId) => this.checkLetter(checkedClueIndex, cellId));
  }

  public checkPuzzle(): void {
    this.getAllClues().forEach((clue, index) => this.checkWord(index));
  }

  public revealLetter(clueIndex?: number, cellId?: string): void {
    const cell = cellId ? this.getCell(cellId) : getCurrentCell(this.data);
    const clue = clueIndex !== undefined ? this.getAllClues()[clueIndex] : getCurrentClue(this.data);
    cell.letter = cell.solution;
    if(cell.type === CellType.Regular && cell.state !== CellState.Correct ){
      cell.state = CellState.Revealed;
    }
    // this.updateFillPercantage();
  }

  public revealWord(clueIndex?: number): void {
    const checkedClueIndex = clueIndex ?? this.getAllClues().indexOf(getCurrentClue(this.data));
    const clue = this.getAllClues()[checkedClueIndex];
    clue.cells.forEach((cellId) => this.revealLetter(checkedClueIndex, cellId));
    this.updateFillPercantage();
  }

  public revealPuzzle(): void {
    this.revealedPuzzle = true;
    this.getAllClues().forEach((clue, index) => this.revealWord(index));
    this.updateFillPercantage();
  }

  public getSecondClue(): IClue | null {
    return getOther(getCurrentClue(this.data), getCluesWithCellIds(this.data, this.data.currentCellId)) || null;
  }

  private updateFillPercantage(): void {
    const regularCells = this.getAllCells().filter((cell) => cell.type === CellType.Regular);
    const filledCells = regularCells.reduce((acc, current) => {
      return acc + (current.letter === current.solution ? 1 : 0);
    }, 0);

    const fillRate = filledCells / regularCells.length;

    this.data.fillRate = fillRate;
  }

  private isInputEnabled(): boolean {
    return !this.data.paused;
  }

  // changeIndex => direction
  private moveCurrentCellInClueAfterInput(changeIndex: number): void {
    const { currentClueId, currentCellId } = this.data;

    const clue = getCurrentClue(this.data)!;
    const currentCellIndex = clue.cells.indexOf(currentCellId);
    
    if (changeIndex > 0) {
      const { skipOverFilledSquares, afterWordMoveTo } = getSettingsData();
      
      const cellsInClue = this.getCellsForClue(clue);
      let clueId = currentClueId; 
      let nextCell: ICell | null = cellsInClue[currentCellIndex + 1] || null;

      const findNextEmptyCell = (cells: ICell[]) => (
        cells.find((cell) => cell.letter === '') || null
      );
      
      if (skipOverFilledSquares) {
        nextCell = findNextEmptyCell(cellsInClue.slice(currentCellIndex));
      }
      
      if (!nextCell && afterWordMoveTo.FirstBlankInSameWord) {
        nextCell = findNextEmptyCell(cellsInClue);
      }

      if (!nextCell && afterWordMoveTo.NextClue) {
        const clueIndex = this.getAllClues().indexOf(clue);
        const nextClue = this.getAllClues()[clueIndex + 1];
        if (nextClue) {
          clueId = nextClue.id;
          nextCell = this.getCellsForClue(nextClue)[0];
        }
      }
      
      if (nextCell) this.setSelection(clueId, nextCell.id);
    } else {
      if (currentCellIndex === 0) return;
      this.setSelection(currentClueId, clue.cells[currentCellIndex - 1]);
    }
  }

  private setClueFromCell(id: string): void {
    const cell = this.getCell(id);
    const prevClue = getCurrentClue(this.data);
    if (cell === null || cell.type !== CellType.Regular) return;

    const clues = getCluesWithCellIds(this.data, id);

    const otherWithPreviousType = clues.find((clue) => clue.type === prevClue.type && clue !== prevClue);

    if (otherWithPreviousType) {
      this.setSelection(otherWithPreviousType.id, id);
    } else {
      const otherClue = getOther(getCurrentClue(this.data), clues);
      this.setSelection(otherClue ? otherClue.id : clues[0].id, id);
    }
  }

  private setSelection(clueId: string, cellId: string): void {
    this.data.currentClueId = clueId;
    this.data.currentCellId = cellId;
    const secondClue = getOther(getCurrentClue(this.data), getCluesWithCellIds(this.data, cellId));
  }

  public getCellsForClue(clue: IClue): ICell[] {
    return clue.cells.map((id) => this.getCell(id)!);
  }

  public getCell(id: string): ICell {
    return this.data.cells.find((cell) => cell.id === id)!;
  }
}
